/**
 * Guided answers → token / overlay adjustments
 */

import type {
  BrandEmotionAxis,
  CopyToneTier,
  ImageStyleTier,
  MotionLevel,
  SiteDesignManifest,
  SiteExperienceGuide,
  SiteImageOverlayManifest,
  SiteTypographyPair,
  SiteVibeId,
  SpaceDensityTier,
} from '@shared/site/types/site-design-manifest';
import {
  buildDesignManifestFromVibe,
  IMAGE_OVERLAY_BY_STYLE,
} from '@shared/site/design/vibe-presets';
import { componentFromSemantic } from '@shared/site/design/token-tier-registry';

const EMOTION_ACCENT: Record<BrandEmotionAxis, string> = {
  cool_trust: '#3b82f6',
  hot_energy: '#f97316',
  warm_authentic: '#d97706',
  precise_future: '#0ea5e9',
};

const DENSITY_SCALE: Record<SpaceDensityTier, number> = {
  open: 1.35,
  balanced: 1,
  dense: 0.75,
};

const MOTION_BY_LEVEL: Record<MotionLevel, { easing: string; durationScale: number }> = {
  none: { easing: 'linear', durationScale: 0 },
  subtle: { easing: 'cubic-bezier(0.4, 0, 0.2, 1)', durationScale: 1 },
  expressive: { easing: 'cubic-bezier(0.4, 0, 0.2, 1)', durationScale: 1.25 },
  dynamic: { easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)', durationScale: 0.75 },
};

const TYPOGRAPHY_FONTS: Record<SiteTypographyPair, { display: string; body: string }> = {
  geometric_sans: {
    display: '"Inter", system-ui, sans-serif',
    body: '"Inter", system-ui, sans-serif',
  },
  display_heavy: {
    display: '"Archivo Black", system-ui, sans-serif',
    body: '"DM Sans", system-ui, sans-serif',
  },
  humanist_serif: {
    display: '"Lora", Georgia, serif',
    body: '"Source Sans 3", system-ui, sans-serif',
  },
  mono_sans: {
    display: '"JetBrains Mono", monospace',
    body: '"IBM Plex Sans", system-ui, sans-serif',
  },
  classic_serif: {
    display: '"Cormorant Garamond", Georgia, serif',
    body: '"Libre Baskerville", Georgia, serif',
  },
};

export function applyExperienceGuideToManifest(
  base: SiteDesignManifest,
  guide: SiteExperienceGuide
): SiteDesignManifest {
  const primitive = { ...base.tokens.primitive };
  primitive['--color-brand-accent'] = EMOTION_ACCENT[guide.emotionAxis];

  const fonts = TYPOGRAPHY_FONTS[guide.typographyPair];
  primitive['--font-display'] = fonts.display;
  primitive['--font-body'] = fonts.body;

  const semantic = {
    ...base.tokens.semantic,
    '--color-accent': 'var(--color-brand-accent)',
    '--font-display': primitive['--font-display'] ?? fonts.display,
    '--font-body': primitive['--font-body'] ?? fonts.body,
  };

  const motion = { ...MOTION_BY_LEVEL[guide.motionLevel] };
  const spacing = { densityScale: DENSITY_SCALE[guide.spaceDensity] };
  const imageOverlay: SiteImageOverlayManifest = {
    ...overlayForImageStyle(guide.imageStyle),
  };

  return {
    ...base,
    experienceGuide: { ...guide },
    tokens: {
      primitive,
      semantic,
      component: componentFromSemantic(semantic),
      motion,
      spacing,
    },
    imageOverlay,
  };
}

function overlayForImageStyle(style: ImageStyleTier): SiteImageOverlayManifest {
  const overlay = IMAGE_OVERLAY_BY_STYLE[style];
  return overlay ?? IMAGE_OVERLAY_BY_STYLE.muted;
}

export function mergeDesignManifestWithVibe(
  current: SiteDesignManifest | undefined,
  vibeId: SiteVibeId
): SiteDesignManifest {
  const fromVibe = buildDesignManifestFromVibe(vibeId);
  if (!current) return fromVibe;
  return {
    ...fromVibe,
    sectionVariants: { ...current.sectionVariants },
  };
}

export function patchExperienceGuide(
  manifest: SiteDesignManifest,
  patch: Partial<SiteExperienceGuide>
): SiteDesignManifest {
  const nextGuide = { ...manifest.experienceGuide, ...patch };
  return applyExperienceGuideToManifest(manifest, nextGuide);
}

/** Copy tone → microcopy hint CSS var (Phase 2+) */
export function copyToneCssHint(tone: CopyToneTier): string {
  const map: Record<CopyToneTier, string> = {
    warm: 'conversational',
    formal: 'direct',
    playful: 'light',
    authoritative: 'dignified',
  };
  return map[tone];
}

const COPY_TONE_HERO_TYPOGRAPHY: Record<
  CopyToneTier,
  {
    titleSize: string;
    subtitleSize: string;
    titleWeight: string;
    titleLetterSpacing: string;
    subtitleLineHeight: string;
  }
> = {
  warm: {
    titleSize: '1.75rem',
    subtitleSize: '1.05rem',
    titleWeight: '600',
    titleLetterSpacing: '0.01em',
    subtitleLineHeight: '1.6',
  },
  formal: {
    titleSize: '1.65rem',
    subtitleSize: '1rem',
    titleWeight: '700',
    titleLetterSpacing: '-0.01em',
    subtitleLineHeight: '1.5',
  },
  playful: {
    titleSize: '2rem',
    subtitleSize: '1.1rem',
    titleWeight: '700',
    titleLetterSpacing: '0.02em',
    subtitleLineHeight: '1.55',
  },
  authoritative: {
    titleSize: '1.85rem',
    subtitleSize: '1rem',
    titleWeight: '800',
    titleLetterSpacing: '0.04em',
    subtitleLineHeight: '1.45',
  },
};

/** Q6 copy tone → hero typography CSS vars for site render */
export function copyToneTypographyVars(tone: CopyToneTier): Record<string, string> {
  const typography = COPY_TONE_HERO_TYPOGRAPHY[tone];
  return {
    '--copy-tone': copyToneCssHint(tone),
    '--hero-title-size': typography.titleSize,
    '--hero-subtitle-size': typography.subtitleSize,
    '--hero-title-weight': typography.titleWeight,
    '--hero-title-letter-spacing': typography.titleLetterSpacing,
    '--hero-subtitle-line-height': typography.subtitleLineHeight,
  };
}
